import { NgClass, NgIf } from '@angular/common';
import { Component, Input } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TenantUserRoles } from '@buspro/shared/common';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NgxPermissionsModule } from 'ngx-permissions';

export interface SidebarNavItem {
  label: string;
  route: string;
  icon: string;
  iconType?: 'fe' | 'fa';
  badge?: number;
  exact?: boolean;
  roles?: TenantUserRoles[]; // Optional: if provided, item is only visible to these roles
}

@Component({
  selector: 'tenant-sidebar-nav-item',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, NgClass, NgIf, NzTooltipDirective, NgxPermissionsModule],
  template: `
    @if (!item.roles || item.roles.length === 0) {
      <li
        [routerLink]="item.route"
        [routerLinkActive]="'active'"
        [routerLinkActiveOptions]="{ exact: item.exact ?? false }"
        [nzTooltipTitle]="isCollapsed ? item.label : null"
        [nzTooltipPlacement]="'right'"
        nz-tooltip
        class="sidebar-nav-item"
        [class.collapsed]="isCollapsed"
      >
        <a class="nav-link">
          <span
            [ngClass]="getIconClasses()"
            class="nav-icon"
          ></span>
          <span class="nav-label" *ngIf="!isCollapsed">{{ item.label }}</span>
          <span class="nav-badge" *ngIf="!isCollapsed && item.badge !== undefined && item.badge > 0">
            {{ item.badge }}
          </span>
        </a>
      </li>
    }
    @if (item.roles && item.roles.length > 0) {
      <li
        *ngxPermissionsOnly="item.roles"
        [routerLink]="item.route"
        [routerLinkActive]="'active'"
        [routerLinkActiveOptions]="{ exact: item.exact ?? false }"
        [nzTooltipTitle]="isCollapsed ? item.label : null"
        [nzTooltipPlacement]="'right'"
        nz-tooltip
        class="sidebar-nav-item"
        [class.collapsed]="isCollapsed"
      >
        <a class="nav-link">
          <span
            [ngClass]="getIconClasses()"
            class="nav-icon"
          ></span>
          <span class="nav-label" *ngIf="!isCollapsed">{{ item.label }}</span>
          <span class="nav-badge" *ngIf="!isCollapsed && item.badge !== undefined && item.badge > 0">
            {{ item.badge }}
          </span>
        </a>
      </li>
    }
  `,
  styles: [
    `
      .sidebar-nav-item {
        position: relative;
        margin: 2px 8px 2px 12px;
        border-radius: 6px;
        transition: all var(--transition-base);
        list-style: none;
        
        .nav-link {
          display: flex;
          align-items: center;
          padding: 9px 12px;
          color: var(--sidebar-text);
          text-decoration: none;
          border-radius: 6px;
          transition: all var(--transition-base);
          position: relative;
          font-size: 14px;
          font-weight: 400;
          
          &:hover {
            background-color: var(--sidebar-hover);
            color: var(--sidebar-text);

            .nav-icon {
              // Follow the label rather than switching hue. This used to set
              // --accent-primary, which in LIGHT mode is #0072BC on a
              // --sidebar-hover of #00629E: blue on blue at 1.28:1, so the icon
              // all but vanished on hover — the reported "dimmed to blue". It
              // also left the label at its normal colour, so only half the row
              // responded. Now the whole row brightens together, and the accent
              // stays reserved for the ACTIVE item, which also has a left bar.
              //
              // transform: scale(1.05) went with it: scaling a 16px icon font
              // lands it off the pixel grid and reads as blur, which is the other
              // half of "dimmed".
              color: inherit;
              opacity: 1;
            }
          }
        }
        
        &.active {
          background-color: var(--sidebar-active);
          
          .nav-link {
            // Was --sidebar-icon-active, which is #3b82f6 in dark mode against a
            // --sidebar-active of #1e3a5f: 3.13:1, below AA for 14px text. The
            // label now uses the sidebar's text colour (9.33:1) and the accent
            // shows up in the icon and the left bar instead. Identical in light
            // mode, where both tokens are #ffffff.
            color: var(--sidebar-text);
            font-weight: 500;
            
            // Left accent bar for active item
            &::before {
              content: '';
              position: absolute;
              left: -6px;
              top: 50%;
              transform: translateY(-50%);
              width: 3px;
              height: 65%;
              background-color: var(--sidebar-active-border);
              border-radius: 0 2px 2px 0;
            }
          }
          
          .nav-icon {
            color: var(--sidebar-icon-active);
            // The resting 0.85 was never reset, so even the active icon was dim.
            opacity: 1;
          }
        }
        
        &.collapsed {
          margin: 2px 4px;
          
          .nav-link {
            justify-content: center;
            padding: 10px;
            width: 40px;
            height: 40px;
          }
          
          .nav-label,
          .nav-badge {
            display: none;
          }
        }
      }
      
      .nav-icon {
        font-size: 16px;
        width: 22px;
        height: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--sidebar-icon);
        transition: all var(--transition-base);
        flex-shrink: 0;
        opacity: 0.85;
      }
      
      .nav-label {
        margin-left: 10px;
        flex: 1;
        font-size: 14px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.4;
      }
      
      .nav-badge {
        margin-left: auto;
        background-color: var(--accent-primary);
        color: white;
        font-size: 10px;
        font-weight: 600;
        padding: 2px 6px;
        border-radius: 10px;
        min-width: 16px;
        text-align: center;
      }
    `,
  ],
})
export class SidebarNavItemComponent {
  @Input() item!: SidebarNavItem;
  @Input() isCollapsed = false;

  getIconClasses(): { [key: string]: boolean } {
    const classes: { [key: string]: boolean } = {};
    if (this.item.iconType !== 'fa') {
      classes['fe'] = true;
    } else {
      classes['fa'] = true;
    }
    classes[this.item.icon] = true;
    return classes;
  }
}
